import React, { type FC, useEffect, useState, type PropsWithChildren } from 'react';

import { isBrowser } from '@lucid/utils';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Icon from '../icon/icon';
import Link from '../link/link';
import Text from '../text/text';

type ABTestAdminUIProps = {
  abTests: {
    [k: string]: Lucid.ABTest.ContextProps;
  };
};

const ABTestAdminUi: FC<PropsWithChildren<ABTestAdminUIProps>> = ({ abTests }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMinified, setIsMinified] = useState(false);
  const [isItemExpanded, setIsItemExpanded] = useState<Record<string, boolean>>({});
  const [currentABTestKeys, setCurrentABTestKeys] = useState<string[]>([]);

  useEffect(() => {
    const abTestKeys = Object.keys(abTests) || [];
    setCurrentABTestKeys(abTestKeys);
  }, [abTests]);

  useEffect(() => {
    currentABTestKeys.map(currentKey =>
      setIsItemExpanded(state => ({
        ...state,
        [currentKey]: false,
      })),
    );
  }, [currentABTestKeys, abTests]);

  const handleCollapseItem = (testName: string, itemState?: boolean) =>
    setIsItemExpanded(state => ({
      ...state,
      [testName]: itemState || !state[testName],
    }));

  const collapseButtonCss = theme => ({
    color: theme.colors.white,
    background: theme.colors.fuchsia[30],
    borderRadius: theme.radii.r4,
    height: '16px',
    width: '24px',
    display: 'flex',
    alighItems: 'center',
    justifyContent: 'center',
    boxShadow: `0px 0px 2px 1px ${theme.colors.yellow[20]}`,
    '&:hover': {
      boxShadow: 'none',
    },
  });

  return (
    <Box
      css={theme => [
        {
          position: 'fixed',
          bottom: 0,
          left: 0,
          zIndex: theme.zIndices.z1000,
          borderTopLeftRadius: theme.radii.r8,
          borderTopRightRadius: theme.radii.r8,
          background: theme.colors.blue[100],
          opacity: '0.9',
          padding: `${theme.space.s8}px 0 ${theme.space.s16}px 0`,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.s4,
          transition: 'transform .2s ease-in-out',
          transform: 'translateY(0)',
        },
        isCollapsed && {
          transform: 'translateY(calc(100% - 32px))',
        },
        isMinified && {
          transform: 'translateY(calc(100% - 32px)) translateX(calc(-100% + 36px))',
        },
      ]}
    >
      <Box
        css={theme => ({
          display: 'flex',
          justifyContent: 'space-between',
          padding: `0 ${theme.space.s8}px`,
          marginBottom: theme.space.s4,
        })}
      >
        <Text
          css={theme => ({
            color: theme.colors.white,
            margin: 0,
          })}
        >
          {`${currentABTestKeys?.length} tests on this page`}
        </Text>
        <Box css={theme => ({ display: 'flex', gap: theme.space.s8 })}>
          <Link
            asButton
            css={theme => collapseButtonCss(theme)}
            onClick={() => setIsMinified(!isMinified)}
            variant="basic"
          >
            <Icon
              color="blue.100"
              css={[
                {
                  height: '12px',
                  width: '60%',
                  transform: isCollapsed ? 'rotate(180deg) translateY(-4px)' : 'rotate(150deg) translateY(-4px)',
                },
                isMinified && { transform: isCollapsed ? 'rotate(0deg)' : 'rotate(-30deg)' },
              ]}
              type="ArrowRight"
            />
          </Link>
          <Link
            asButton
            css={theme => [collapseButtonCss(theme), isMinified && { display: 'none' }]}
            onClick={() => setIsCollapsed(!isCollapsed)}
            variant="basic"
          >
            <Icon
              color="blue.100"
              css={[
                { height: '12px', transform: 'rotate(0) translateY(0)' },
                isCollapsed && { transform: 'rotate(180deg) translateY(-4px)' },
              ]}
              type="CarotDown"
            />
          </Link>
        </Box>
      </Box>
      {currentABTestKeys.map(abTestKey => {
        const currentTest = abTests[abTestKey];
        const isOtherTestExpanded = Object.keys(isItemExpanded)
          .filter(k => k !== abTestKey)
          .some(k => isItemExpanded[k]);

        const {
          branch,
          branches,
          branchesToRender = [],
          description,
          hasTriggerFired,
          isTestActive,
          handleUpdateBranch,
          trigger,
        } = currentTest;
        const currentBranchKeys = Object.keys(branches || {}) as Array<Lucid.ABTest.VariationKeys>;
        const currentAvailableBranches = currentBranchKeys.filter(
          curBranch => branches && branches[curBranch] !== null && branchesToRender.includes(curBranch),
        );

        return (
          <Box
            key={abTestKey}
            css={theme => [
              {
                display: 'flex',
                gap: theme.space.s4,
                height: '40px',
                padding: `0 ${theme.space.s8}px`,
              },
              isOtherTestExpanded && { opacity: '0.3' },
            ]}
          >
            <Box
              css={theme => [
                {
                  width: '20px',
                  height: '100%',
                  flex: '0 1 20px',
                  border: `1px solid ${theme.colors.coolGrey[40]}`,
                  borderRadius: theme.radii.r4,
                  background: theme.colors.red[20],
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                },
                isTestActive && {
                  background: theme.colors.lime[20],
                },
              ]}
              onMouseLeave={() => handleCollapseItem(abTestKey, false)}
              onMouseOver={() => handleCollapseItem(abTestKey, true)}
            >
              <Icon color="coolGrey.60" type="Info" />
              {isItemExpanded[abTestKey] && (
                <Box
                  css={theme => ({
                    paddingLeft: theme.space.s8,
                    position: 'absolute',
                    left: '15px',
                    bottom: 0,
                    width: '274px',
                    zIndex: theme.zIndices.z1000,
                  })}
                >
                  <Box
                    css={theme => ({
                      background: theme.colors.blue[5],
                      padding: `${theme.space.s8}px ${theme.space.s8}px 0`,
                      borderRadius: theme.radii.r4,
                      borderBottomRightRadius: 0,
                      border: `1px solid ${theme.colors.teal[40]}`,
                      borderBottom: 'none',
                    })}
                  >
                    <Text
                      css={theme => ({
                        wordBreak: 'break-word',
                        position: 'relative',
                        color: theme.colors.white,
                        zIndex: 1,
                        fontSize: `min(20px, calc(24rem / ${abTestKey.length}))`,
                        fontFamily: 'Menlo, monospace',
                        '&::before': {
                          zIndex: -1,
                          content: '""',
                          position: 'absolute',
                          top: `-${theme.space.s8}px`,
                          left: `-${theme.space.s8}px`,
                          height: 'calc(100% + 16px)',
                          width: 'calc(100% + 16px)',
                          borderTopLeftRadius: theme.radii.r4,
                          borderTopRightRadius: theme.radii.r4,
                          background: theme.colors.blue[100],
                        },
                      })}
                      variant="paragraphS400"
                    >
                      {abTestKey}
                    </Text>
                    <Text
                      css={theme => ({
                        color: isTestActive ? theme.colors.teal[60] : theme.colors.coolGrey[50],
                        marginBottom: theme.space.s4,
                      })}
                      variant="labelXS500"
                    >
                      {`${isTestActive ? '*Active' : '*Inactive'}`}
                    </Text>
                    <Text
                      css={theme => ({ color: isTestActive ? theme.colors.teal[60] : theme.colors.coolGrey[50] })}
                      variant="labelXS500"
                    >
                      {`*${hasTriggerFired && isTestActive ? 'Was assigned' : 'Will assign'} on ${trigger}`}
                    </Text>
                    <Text variant="paragraphXS400">{description}</Text>
                    <Box css={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                      <Link
                        css={theme => ({
                          marginBottom: theme.space.s16,
                          color: theme.colors.indigo[70],
                          fontSize: theme.fontSizes.fs12,
                        })}
                        href={`https://internal-admin.lucid.app/feature/details/${abTestKey}`}
                        target="blank"
                        variant="primaryAlternate"
                      >
                        Configure in Admin.
                      </Link>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            <Box
              as="span"
              css={theme => ({
                height: '100%',
                padding: theme.space.s8,
                paddingRight: theme.space.s48,
                borderRadius: theme.radii.r4,
                background: theme.colors.blue[5],
                border: `1px solid ${theme.colors.teal[40]}`,
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                gap: theme.space.s16,
                overflow: 'hidden',
                position: 'relative',
              })}
            >
              <Text
                css={{
                  width: '250px',
                  margin: 0,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
                variant="paragraphS400"
              >
                {abTestKey}
              </Text>
              <Box
                css={theme => ({
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: theme.colors.coolGrey[100],
                  minWidth: '24px',
                  height: '100%',
                  color: 'white',
                  borderRadius: 'inherit',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${branchesToRender.length}, 1fr)`,
                  transform: `translateX(${40 * (branchesToRender.length - 1)}px)`,
                  transition: 'transform .2s ease-in-out',
                  borderLeft: `1px solid ${theme.colors.teal[40]}`,
                  [`& > .${branch}-is-selected`]: {
                    transition: 'transform .2s ease-in-out',
                    transform: `translateX(calc(-40px * ${branchesToRender.findIndex(b => b === branch)}))`,
                  },
                  '&:hover': {
                    transform: 'translateX(0)',
                    [`& > .${branch}-is-selected`]: {
                      transform: 'translateX(0)',
                    },
                  },
                })}
              >
                {currentAvailableBranches.map(currentBranch => (
                  <Link
                    key={`${abTestKey}-${currentBranch}`}
                    asButton
                    className={branch === currentBranch ? `${currentBranch}-is-selected` : currentBranch}
                    css={theme => [
                      {
                        background: theme.colors.coolGrey[100],
                        width: '40px',
                        color: theme.colors.white,
                        opacity: '0.6',
                        '&:hover': {
                          opacity: 1,
                          boxShadow: `inset 0 0 2px 1px ${theme.colors.teal[40]}`,
                        },
                      },
                      branch === currentBranch && {
                        background: theme.colors.indigo[70],
                        opacity: '1',
                        '&:hover': {
                          boxShadow: 'none',
                        },
                      },
                    ]}
                    onClick={() => {
                      const storageKey = `abTest_${currentBranch}`;

                      if (abTestKey && isBrowser()) {
                        window.localStorage.setItem(storageKey, currentBranch);
                      }

                      return handleUpdateBranch(currentBranch);
                    }}
                    variant="basic"
                  >
                    {currentBranch}
                  </Link>
                ))}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ABTestAdminUi;
